// BookTickets/stages/PaymentProcess/PaymentMethods.tsx
'use client'

import { useCallback, useEffect, useState } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'

import styles from './index.module.scss'

import CardIcon from './card-icon.svg'
import PaypalIcon from './paypal-icon.svg'
import MbWayIcon from './mbway-icon.svg'

import trackGA4 from '@booking/services/trackingGa4'
import { useBookingState } from '@booking/state/booking.context'

type PaymentMethodKey = 'card' | 'paypal' | 'mbway'

type PaymentMethodsProps = {
  onPayWithCard: () => Promise<void> | void
  onPayWithPaypal: () => Promise<void> | void
  onPayWithMbWay: () => Promise<void> | void
}

export default function PaymentMethods({
  onPayWithCard,
  onPayWithPaypal,
  onPayWithMbWay,
}: PaymentMethodsProps) {
  const t = useTranslations('booking')
  const { cart } = useBookingState()

  const [method, setMethod] = useState<PaymentMethodKey | null>(null)
  const [switching, setSwitching] = useState(false)

  const disabled = Boolean(cart && Number(cart.total) === 0)

  useEffect(() => {
    if (!method) return
    trackGA4.selectPaymentMethod?.(method)
  }, [method])

  const run = useCallback(
    async (m: PaymentMethodKey, fn: () => Promise<void> | void) => {
      if (switching || disabled) return
      setMethod(m)
      setSwitching(true)
      try {
        await fn()
      } finally {
        // PayPal usually navigates away immediately — this finally will still run if it doesn’t.
        setSwitching(false)
      }
    },
    [disabled, switching],
  )

  const label = useCallback(
    (m: PaymentMethodKey) => {
      const waiting = switching && method === m
      if (waiting)
        return <span className={cn(styles.switching)}>{t('ticket.payment.pleaseWait')}</span>

      if (m === 'card') return t('ticket.payment.creditDebitCard')
      if (m === 'paypal') return 'PayPal'
      return 'MB WAY'
    },
    [method, switching, t],
  )

  return (
    <div className={cn(styles.methods, method && styles.hasSelected)}>
      <button
        type="button"
        data-id="card-payment-button"
        className={cn(styles.button, method === 'card' && styles.selected)}
        disabled={switching || disabled}
        onClick={() => run('card', onPayWithCard)}
      >
        <CardIcon width="25" />
        {label('card')}
      </button>

      <button
        type="button"
        data-id="paypal-payment-button"
        className={cn(styles.button, method === 'paypal' && styles.selected)}
        disabled={switching || disabled}
        onClick={() => run('paypal', onPayWithPaypal)}
      >
        <PaypalIcon width="15" />
        {label('paypal')}
      </button>

      <button
        type="button"
        data-id="mbway-payment-button"
        className={cn(styles.button, method === 'mbway' && styles.selected)}
        disabled={switching || disabled}
        onClick={() => run('mbway', onPayWithMbWay)}
      >
        <MbWayIcon width="20" />
        {label('mbway')}
      </button>
    </div>
  )
}
